import {Component, Emitter} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';
import { DataChannelError, RPCError } from './utils/intiface-errors.js';

const MODULE = 'RPC';

/** RPC request message sent over the data channel to invoke a remote method */
type RPCRequest = {
        type: "request";
        id: string;
        method: string;
        params?: any;
    };

/** RPC response message sent back after processing a request */
type RPCResponse = {
    type: "response";
    id: string;
    result?: any;
    error?: { code: number; message: string; data?: any };
};

/** Union type representing either an RPC request or response */
type RPCMessage = RPCRequest | RPCResponse;

/**
 * IntifaceRPCNode
 * Base class for RPC communication over WebRTC data channels.
 * Provides method registration, remote procedure calls, and message handling.
 */
export abstract class IntifaceRPCNode {
    static TypeName = 'IntifaceRPCNode';
    
    /** Maximum number of ICE candidates to buffer before remote description is set */
    protected static readonly MAX_REMOTE_CANDIDATES = 100;
    
    /** WebRTC peer connection instance */
    protected intifacePC: RTCPeerConnection | undefined;
    /** WebRTC data channel for RPC communication */
    protected intifaceDC: RTCDataChannel | null = null;
    /** Buffer for ICE candidates received before remote description is set */
    protected remoteCandidates: RTCIceCandidateInit[] = [];


    /** Emits when the data channel is ready for communication */
    public onDataChannelReady: Emitter = new Emitter();

    /** Whether the data channel is open and ready for sending messages */
    protected dcReady = false;
    /** Counter for generating unique request IDs */
    private nextId = 1;
    /** Map of pending RPC calls awaiting responses */
    private pending = new Map<
        string,
        { resolve: (v: any) => void; reject: (e: any) => void; timeoutId: number | null }
    >();
    /** Map of registered method handlers that can be called remotely */
    private handlers = new Map<string, (params: any) => Promise<any> | any>();
    /** Timeout duration in milliseconds for RPC calls */
    private callTimeoutMs: number = 30000;

    /**
     * Register a method that can be invoked by the remote peer.
     * @param name - Method name that remote peer will use to invoke this handler
     * @param handler - Function to execute when method is called (can be sync or async)
     */
    public registerMethod(name: string, handler: (params: any) => Promise<any> | any) {
        this.handlers.set(name, handler);
    }

    /**
     * Unregister a previously registered method.
     * @param name - Method name to remove from handler registry
     */
    public unregisterMethod(name: string) {
        this.handlers.delete(name);
    }

    /**
     * Invoke a method on the remote peer and wait for the result.
     * @param method - Name of the remote method to call
     * @param params - Parameters to pass to the remote method (optional)
     * @returns Promise resolving to the method's return value
     * @throws {Error} If data channel not ready, call times out, or remote method returns error
     */
    call(method: string, params?: any): Promise<any> {
        if (!this.intifaceDC || !this.dcReady) {
            return Promise.reject(new DataChannelError(
                "DataChannel not established",
                this.intifaceDC?.readyState
            ));
        }

        const id = `${Date.now()}-${this.nextId++}`;
        const req: RPCRequest = { type: "request", id, method, params };

        Logger.debug(`Calling ${method}`, MODULE);

        return new Promise((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                this.pending.delete(id);
                reject(new RPCError(`RPC call timeout after ${this.callTimeoutMs}ms`, method, params));
            }, this.callTimeoutMs);

            this.pending.set(id, { resolve, reject, timeoutId });
            try {
                Logger.debug(`Sending ${method} via datachannel`, MODULE);
                this.intifaceDC!.send(JSON.stringify(req));
            } catch (err) {
                window.clearTimeout(timeoutId);
                this.pending.delete(id);
                reject(new RPCError(`Failed to send RPC request`, method, params));
            }
        });
    }

    /**
     * Close the RPC connection and clean up all resources.
     * Rejects all pending calls, clears handlers, and closes data channel and peer connection.
     */
    async close() {
        this.pending.forEach(({ reject, timeoutId }) => {
            if (timeoutId) window.clearTimeout(timeoutId);
            reject(new RPCError("Connection closed before response received"));
        });
        this.pending.clear();
        this.handlers.clear();
        if (this.intifaceDC) {
            try {
                this.intifaceDC.close();
            } catch (err) {
                Logger.warn('Failed to close data channel', MODULE, err);
            }
            this.intifaceDC = null;
        }
        if (this.intifacePC) {
            try {
                this.intifacePC.close();
            } catch (err) {
                Logger.warn('Failed to close peer connection', MODULE, err);
            }
            this.intifacePC = undefined;
        }
        this.dcReady = false;
    }

    /**
     * Configure event handlers for a WebRTC data channel.
     * Sets up open, close, error, and message handlers for RPC communication.
     * @param dc - RTCDataChannel instance to configure
     */
    protected async setupDataChannel(dc: RTCDataChannel) {
        dc.onopen = () => {
            this.dcReady = true;
            Logger.info('DataChannel open', MODULE);
            this.onDataChannelReady.notify();
        };

        dc.onclose = () => {
            this.dcReady = false;
            Logger.info('DataChannel closed', MODULE);
        };

        dc.onerror = (ev) => {
            Logger.error('DataChannel error', MODULE, ev);
        };

        dc.onmessage = (ev) => {
            Logger.debug('DataChannel message received', MODULE);
            this.handleDataMessage(ev.data);
        };
    }

    /**
     * Process incoming RPC messages on the data channel.
     * Handles both responses to our calls and incoming requests from remote peer.
     * @param raw - Raw message data (string or object) from data channel
     */
    private async handleDataMessage(raw: any) {
        let msg: RPCMessage;
        try {
            if (typeof raw === "string") msg = JSON.parse(raw);
            else msg = raw;
        } catch (err) {
            Logger.warn('Received invalid JSON on datachannel', MODULE, raw);
            return;
        }

        if (!msg || typeof msg !== "object" || !("type" in msg) || !("id" in msg)) {
            Logger.warn('Malformed RPC message', MODULE, msg);
            return;
        }

        if (msg.type === "response") {
            const resp = msg as RPCResponse;
            const pending = this.pending.get(resp.id);
            if (!pending) {
                Logger.warn(`No pending RPC for response id ${resp.id}`, MODULE);
                return;
            }
            if (pending.timeoutId) window.clearTimeout(pending.timeoutId);
            this.pending.delete(resp.id);

            if (resp.error) pending.reject(new Error(resp.error.message || "RPC error"));
            else pending.resolve(resp.result);
            return;
        }

        if (msg.type === "request") {
            const req = msg as RPCRequest;
            const handler = this.handlers.get(req.method);
            Logger.debug(`Handling ${req.method}`, MODULE);
            if (!handler) {
                // respond with method not found
                const errorResp: RPCResponse = {
                    type: "response",
                    id: req.id,
                    error: { code: 404, message: `Method not found: ${req.method}` },
                };
                this.intifaceDC?.send(JSON.stringify(errorResp));
                return;
            }

            try {
                const result = await Promise.resolve(handler(req.params));
                const resp: RPCResponse = { type: "response", id: req.id, result };
                Logger.debug(`Sending response for ${req.method}`, MODULE);
                this.intifaceDC?.send(JSON.stringify(resp));
            } catch (err: any) {
                Logger.error(`Handler error for ${req.method}`, MODULE, err);
                const rpcError = new RPCError(
                    err?.message ?? "Handler error",
                    req.method,
                    req.params
                );
                const resp: RPCResponse = {
                    type: "response",
                    id: req.id,
                    error: { code: 500, message: rpcError.message, data: err },
                };
                this.intifaceDC?.send(JSON.stringify(resp));
            }
            return;
        }
    }
}
